import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { useAuth } from '../auth/auth-store';
import { formatDateTime, formatTime } from '../ui/format';
import { canOrganise, minutesUntil, moveTo, SNOOZE_CHOICES, type Move } from './reminder';
import { deleteEvent, getEvent, snoozeEvent, updateEvent } from './api';

/**
 * The reminder, as something you can act on (Phase 125).
 *
 * A meeting alert used to be a toast that said "o compromisso X começa em breve" and faded after
 * eight seconds. That is a notification, not a reminder: the moment somebody is told is exactly the
 * moment they know whether they can make it, and everything they want to do about it — postpone the
 * alert, push the meeting, call it off — was somewhere else on another screen.
 *
 * So it is a dialog, and it does not fade. It is the one notification in this product allowed to
 * interrupt, because it is the one with a deadline attached.
 *
 * What it offers depends on who is looking. Anybody invited may postpone **their own** alert;
 * moving or cancelling the meeting is offered only to somebody who may actually do it, because a
 * button that exists to produce a refusal reads as the product being broken rather than as the rule
 * it is.
 */
export function EventReminder({
  eventId,
  onClose,
}: {
  eventId: string;
  /**
   * Closing it, and whether the bell row it came from should be marked read.
   *
   * Always true today — every way out of this dialog is somebody having dealt with it — but the
   * caller owns the notification and this one owns the meeting, and saying so keeps the reason
   * ("the bell refuses a second alert while the first is unread") in one place.
   */
  onClose: (input: { markRead: boolean }) => void;
}): React.JSX.Element | null {
  const client = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);

  const event = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => getEvent(eventId),
    retry: false,
  });

  const settled = (): void => {
    void client.invalidateQueries({ queryKey: ['calendar'] });
    void client.invalidateQueries({ queryKey: ['agenda'] });
  };

  const snooze = useMutation({
    mutationFn: (minutes: number) => snoozeEvent(eventId, minutes),
    /*
     * Marked read on the way out, and that is not housekeeping.
     *
     * The bell refuses a second notification about the same thing while the first is unread, so an
     * alert that was postponed and left unread could never come back — the snooze would fire into
     * a dedup and nothing would arrive. "Adiar" means "I have seen this, tell me again later",
     * which is exactly what marking it read says.
     */
    onSuccess: () => onClose({ markRead: true }),
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const move = useMutation({
    mutationFn: (which: Move) => {
      const current = event.data!;

      return updateEvent(eventId, moveTo(current.startsAt, current.endsAt, which));
    },
    onSuccess: () => {
      settled();
      onClose({ markRead: true });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const cancel = useMutation({
    mutationFn: () => deleteEvent(eventId),
    onSuccess: () => {
      settled();
      onClose({ markRead: true });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  // An event that has been deleted while the alert was on screen has nothing left to say about it.
  if (event.isError) return null;

  const detail = event.data;
  const away = detail ? minutesUntil(detail.startsAt) : 0;
  const mine = detail ? canOrganise(detail, { userId: user?.id ?? null }) : false;
  const busy = snooze.isPending || move.isPending || cancel.isPending;

  return (
    <div className="reminder" role="dialog" aria-label={t('reminder.title')}>
      <div className="reminder__head">
        <span className="reminder__badge">{t('reminder.title')}</span>

        <button
          type="button"
          className="reminder__close"
          aria-label={t('reminder.silence')}
          title={t('reminder.silence')}
          onClick={() => onClose({ markRead: true })}
        >
          ×
        </button>
      </div>

      {detail ? (
        <>
          <p className="reminder__when">
            {away > 0
              ? t('reminder.inMinutes').replace('{n}', String(away))
              : away === 0
                ? t('reminder.now')
                : t('reminder.startedAgo').replace('{n}', String(Math.abs(away)))}
          </p>

          <p className="reminder__event">{detail.title}</p>

          <p className="muted small">
            {formatTime(detail.startsAt)} – {formatTime(detail.endsAt)}
            {detail.location && ` · ${detail.location}`}
          </p>

          {error && (
            <p className="alert alert--compact" role="alert">
              {error}
            </p>
          )}

          {/* The one action that is about the meeting rather than about the alert. */}
          {detail.conferenceUrl && (
            <a
              className="button button--primary button--compact reminder__join"
              href={detail.conferenceUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => onClose({ markRead: true })}
            >
              {t('reminder.join')}
            </a>
          )}

          <div className="reminder__group">
            <span className="reminder__label">{t('reminder.snooze')}</span>

            <span className="reminder__buttons">
              {SNOOZE_CHOICES.map((minutes) => (
                <button
                  key={minutes}
                  type="button"
                  className="button button--quiet button--compact"
                  disabled={busy}
                  onClick={() => snooze.mutate(minutes)}
                >
                  {t('reminder.minutes').replace('{n}', String(minutes))}
                </button>
              ))}
            </span>
          </div>

          {mine && (
            <div className="reminder__group">
              <span className="reminder__label">{t('reminder.reschedule')}</span>

              <span className="reminder__buttons">
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  disabled={busy}
                  onClick={() => move.mutate('PLUS_30')}
                >
                  {t('reminder.plus30')}
                </button>

                <button
                  type="button"
                  className="button button--quiet button--compact"
                  disabled={busy}
                  onClick={() => move.mutate('PLUS_60')}
                >
                  {t('reminder.plus60')}
                </button>

                <button
                  type="button"
                  className="button button--quiet button--compact"
                  disabled={busy}
                  onClick={() => move.mutate('TOMORROW')}
                >
                  {t('reminder.tomorrow')}
                </button>

                {/* Anything else is the event's own screen, which already knows every rule. */}
                <button
                  type="button"
                  className="button button--quiet button--compact"
                  onClick={() => {
                    onClose({ markRead: true });
                    navigate('/agenda');
                  }}
                >
                  {t('reminder.otherTime')}
                </button>
              </span>
            </div>
          )}

          <div className="reminder__foot">
            <span className="muted small">{formatDateTime(detail.startsAt)}</span>

            {mine && (
              <button
                type="button"
                className="button button--quiet button--compact text-down"
                disabled={busy}
                onClick={() => cancel.mutate()}
              >
                {t('reminder.cancelEvent')}
              </button>
            )}
          </div>
        </>
      ) : (
        <p className="muted small">{t('state.loading')}</p>
      )}
    </div>
  );
}
